<template>
  <div class="comment-sender" ref="sender">
    <template v-if="isLogined">
      <textarea
        ref="input"
        class="comment-sender_input"
        :placeholder="$t('comment.sender_input_placeholder')"
        v-model="comment"
        :maxlength="commentMaxLength"
        @keydown="changeComment"
        @keyup="formatComment"
      />
      <p class="comment-sender_mock">{{comment}}</p>
      <div class="comment-sender_actions">
        <div class="comment-sender_limit">{{comment.length}}/{{commentMaxLength}}</div>
        <button
          class="comment-sender_send"
          :class="{enabled: comment.trim().length > 0 && !isSending}"
          @click="sendComment"
        >{{$t('comment.sended_send')}}</button>
      </div>
    </template>
    <template v-else>
      <button class="comment-sender_login" @click="login">{{$t('comment.login_before_send')}}</button>
    </template>
  </div>
</template>

<script >
import { defineComponent, ref, computed } from 'vue';
import commentSrv from '@/service/comment';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import toast from '@/components/toast';

export default defineComponent({
  setup(props, context) {
    const comment = ref('');
    const commentMaxLength = ref(200);
    const input = ref(null);
    const sender = ref(null);
    const isSending = ref(false);

    const isLogined = computed(() => !!context.attrs.mid);

    const roomId = computed(() => context.attrs.roomId);

    const sendComment = async () => {
      if (isSending.value) {
        return;
      }
      isSending.value = true;

      try {
        const commentToSend = comment.value.trim();
        if (!commentToSend) {
          return;
        }
        if (!isLogined.value) {
          login();
          return;
        }
        if (!roomId.value) {
          throw new Error('Sender: Please check live room id');
        }
        const resp = await commentSrv.sendComment(roomId.value, commentToSend);
        emitter.emit(EB_EVENT_NAME.COMMENT_SENT);
      } catch (err) {
        console.warn(err);
        toast({
          duration: 3000,
          appendTo: '.live-comment-container',
          message: [-504, -412].includes(err.code) ? 'Network Error' : err.message,
        });
      } finally {
        isSending.value = false;
        comment.value = '';
      }
    };

    const changeComment = (event) => {
      if (event.keyCode === 13) {
        event.preventDefault();
        sendComment();
      }
    };

    const formatComment = () => {
      if (comment.value.includes('\n')) {
        comment.value = comment.value.replace(/\n/g, '');
      }
    };

    const login = () => {
      context.emit('login');
    };

    return {
      // refs
      sender,
      input,
      // states
      comment,
      commentMaxLength,
      isLogined,
      isSending,
      // actions
      sendComment,
      changeComment,
      formatComment,
      login,
    };
  },
});
</script>


<style lang="less" scoped>
@import "@/styles/polyfill/gap.less";

.comment-sender {
  font-size: 14px;
  display: flex;
  .flex-gap(12px, 'column nowrap');
  flex-direction: column;
  position: relative;

  .input-content {
    font-family: inherit;
    background: rgba(153, 153, 153, 0.08);
    border-radius: 4px;
    border: none;
    line-height: 14px;
    color: #333;
    font-size: 12px;
    padding: 10px 12px;
    min-height: 34px;
    max-height: 100px;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    resize: none;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;

    &:focus {
      outline: 1px solid rgba(76, 147, 255, 0.3);
    }
  }

  &_input {
    position: absolute;
    z-index: 2;
    left: 0;
    top: 0;
    right: 0;
    bottom: 30px;
    .input-content();

    &:focus-visible {
      outline: none;
    }
    &::placeholder {
      color: #999;
    }
  }
  &_mock {
    visibility: hidden;
    .input-content();
  }

  &_actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    .flex-gap(12px);
  }
  &_limit {
    font-size: 12px;
    color: rgba(153, 153, 153, 0.9);
    cursor: default;
  }
  &_send {
    color: rgba(34, 34, 34, 0.9);
    cursor: pointer;
    border: none;
    background: transparent;

    &.enabled {
      color: #4c93ff;
    }
    &:focus-visible {
      outline: none;
    }
  }
  &_login {
    background: #5d9dff;
    border-radius: 4px;
    color: #fff;
    font-weight: 700;
    border: none;
    height: 52px;
    cursor: pointer;
    text-align: center;
    padding: 0 24px;
    margin: 0;
  }
}
</style>